import {
  type OpenerKind,
  type OpenerThemeOverrides,
  SAMPLE_OPENER_BOX,
} from "@loot-os/api-contract";
import { type BoxOpener, LootBoxOpener, type LootTheme } from "@loot-os/react";
import { useMemo } from "react";

import { previewOpenerClient } from "../lib/previewOpenerClient";

/** The sample box never changes, so one client serves every preview. */
const createSampleClient = previewOpenerClient(SAMPLE_OPENER_BOX);

/**
 * The real opener, in demo mode on a made-up box, themed the way a shopper would see a style: the
 * host's theme (`hostTheme`, undefined = the opener's own palette) with the style's overrides on
 * top, wearing the environment's logo (`logoSvg`) or brand name (`brandName`) as a box there would. Opens roll locally;
 * nothing is written.
 */
export function OpenerStylePreview({
  kind,
  hostTheme,
  overrides,
  logoSvg = "",
  brandName = "",
}: {
  kind: OpenerKind;
  hostTheme: LootTheme | undefined;
  overrides: OpenerThemeOverrides | null;
  /** The environment's opener logo as SVG source; "" = none (and every platform-scope preview). */
  logoSvg?: string;
  /** The environment's opener brand name; "" = none (and every platform-scope preview). */
  brandName?: string;
}) {
  // The style rides on the opener's `opener` prop, which the opener resolves exactly as it does a
  // box's own style: a keystroke re-themes and a new kind swaps the animation, with no refetch.
  const overridesKey = JSON.stringify(overrides);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialized overrides
  const opener = useMemo<BoxOpener>(
    () => ({
      styleId: "opener-style-preview",
      kind,
      ...(overrides ? { theme: overrides } : {}),
      ...(logoSvg ? { logoSvg } : {}),
      ...(brandName ? { brandName } : {}),
    }),
    [kind, overridesKey, logoSvg, brandName],
  );
  return (
    <LootBoxOpener
      boxId={SAMPLE_OPENER_BOX.id}
      createClient={createSampleClient}
      // Non-empty so the SDK calls the factory; the factory answers locally.
      publishableKey="preview"
      sessionToken={null}
      theme={hostTheme}
      opener={opener}
      maxQuantity={1}
      defaultQuantity={1}
      // Sound on, as a shopper hears it: the host sets no sound files, so the spinner stays
      // silent as it is on the storefront, and a kind that synthesizes its own (Neon Slam) plays.
      showFairness={false}
    />
  );
}
